import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import FaqAccordion from "../components/FaqAccordion";
import CtaBand from "../components/CtaBand";
import ArticleCard from "../components/ArticleCard";
import NotFound from "./NotFound";
import keepPaths from "../data/keepPaths.json";

const KEEP = new Set(keepPaths);
import { Reveal, Eyebrow, ChapterHeading } from "../components/Reveal";
import { SITE, getLocation, getService, serviceImage, hash } from "../data/site";
import { cityServiceContent } from "../data/content";
import { articlesForService } from "../data/articles";

export default function CityServicePage() {
  const { citySlug, serviceSlug } = useParams();
  const loc = getLocation(citySlug);
  const svc = getService(serviceSlug);
  if (!loc || !svc) return <NotFound />;

  const content = cityServiceContent(loc, svc);
  const img = serviceImage(svc, hash(`${loc.slug}${svc.slug}`));
  const articles = articlesForService(svc.slug, 2);

  return (
    <>
      <Seo
        title={`${svc.title} in ${loc.name} | SIA Licensed | Xtreme Protection England`}
        description={`${svc.title} in ${loc.name}, ${loc.county} — ${svc.tagline} SIA licensed, BS 7858 vetted, 24/7 across ${loc.region}. Call ${SITE.phone}.`}
        keywords={[
          ...svc.keywords.map((k) => `${k} ${loc.name}`),
          `${svc.keywords[0]} ${loc.county}`,
          `security services ${loc.name}`,
          `security company ${loc.name}`,
        ].join(", ")}
        canonical={`${SITE.domain}/locations/${loc.slug}/${svc.slug}`}
        noindex={!KEEP.has(`/locations/${loc.slug}/${svc.slug}`)}
      />

      <section className="relative tactical-grid border-b border-white/10 overflow-hidden" data-testid="cityservice-hero">
        <div className="absolute inset-0">
          <img src={img.url} alt={`${svc.title} in ${loc.name} — ${img.alt}`} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/70 to-ink"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <nav className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber transition-colors">Home</Link>
            <span className="mx-2 text-amber">//</span>
            <Link to={`/locations/${loc.slug}`} className="hover:text-amber transition-colors">{loc.name}</Link>
            <span className="mx-2 text-amber">//</span>
            <span className="text-white">{svc.title}</span>
          </nav>
          <Reveal>
            <Eyebrow>{svc.title} // {loc.name}, {loc.county}</Eyebrow>
            <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl text-white max-w-4xl">
              {svc.title} in {loc.name}
            </h1>
            {content.intro.slice(0, 2).map((p, i) => (
              <p key={i} className="mt-6 max-w-3xl text-base sm:text-lg text-gray-300 leading-relaxed">
                {p}
              </p>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="cityservice-detail">
        <ChapterHeading number="01" label="DEPLOYMENT DETAIL" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal className="space-y-5">
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              {content.intro[2]}
            </p>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              {content.intro[3]}
            </p>
            <h2 className="font-display font-bold uppercase tracking-wide text-xl text-white pt-2">
              Sectors we protect in {loc.name}
            </h2>
            <div className="flex flex-wrap gap-2" data-testid="cityservice-sectors">
              {svc.sectors.map((s) => (
                <span key={s} className="font-mono text-[10px] uppercase tracking-widest text-gray-400 border border-white/10 px-2.5 py-1.5">
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-bold uppercase tracking-wide text-xl text-white mb-5">
              What&apos;s included in {loc.name}
            </h2>
            <ul className="space-y-3" data-testid="cityservice-includes">
              {svc.includes.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-amber mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-20" data-testid="cityservice-links">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="02" label="RELATED COVERAGE" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <Reveal>
              <h2 className="font-display font-bold uppercase tracking-wide text-xl text-white mb-5">
                Other security services in {loc.name}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cityservice-other-services">
                {content.otherServices.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/locations/${loc.slug}/${s.slug}`}
                    className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-4 py-3.5 hover:border-amber transition-colors duration-300"
                  >
                    <span className="font-display font-bold uppercase tracking-wide text-sm text-white group-hover:text-amber transition-colors">
                      {s.short} {loc.name}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-amber shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
              <Link
                to={`/locations/${loc.slug}`}
                className="group mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-amber hover:text-white transition-colors"
              >
                All security services in {loc.name}
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-bold uppercase tracking-wide text-xl text-white mb-5">
                {svc.short} near {loc.name}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cityservice-nearby">
                {content.nearby.map((n) => (
                  <Link
                    key={n.slug}
                    to={`/locations/${n.slug}/${svc.slug}`}
                    className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-4 py-3.5 hover:border-amber transition-colors duration-300"
                  >
                    <span>
                      <span className="block font-display font-bold uppercase tracking-wide text-sm text-white group-hover:text-amber transition-colors">
                        {svc.short} {n.name}
                      </span>
                      <span className="block font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-0.5">
                        {n.county}
                      </span>
                    </span>
                    <MapPin className="w-3.5 h-3.5 text-amber shrink-0" />
                  </Link>
                ))}
              </div>
              <Link
                to={`/services/${svc.slug}`}
                className="group mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-amber hover:text-white transition-colors"
              >
                {svc.title} across England
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 sm:py-20" data-testid="cityservice-intel">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="03" label="FROM THE INTEL DESK" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white leading-tight mb-8">
              Reading on {svc.short.toLowerCase()}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
            {articles.map((a) => (
              <ArticleCard key={a.slug} article={a} prefix="cityservice-article" />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-20" data-testid="cityservice-faq">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="04" label="QUESTIONS" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight mb-10">
              {svc.short} in {loc.name} — answered
            </h2>
            <FaqAccordion faqs={content.faqs} testidPrefix="cityservice-faq" />
          </Reveal>
        </div>
      </section>

      <CtaBand
        title={`Book ${svc.short.toLowerCase()} in ${loc.name}`}
        text={`Free risk assessment and a tailored written quote for ${svc.title.toLowerCase()} in ${loc.name}. Call ${SITE.phone} or email ${SITE.email} — 24/7.`}
      />
    </>
  );
}
